import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import {Box, Flex, Heading, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Swiper} from '@amblique/base/app/components/shared/swiper';

export const Collections = ({
  title = 'Discover our furniture',
  description = 'Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus.',
  hideNavigation = false,
  collections,
  onCollectionClick,
  ...props
}) => {
  const styles = useMultiStyleConfig('Collections', {});

  // Memoized default collections to prevent recreation and module-level side effects
  const defaultCollections = useMemo(() => [
    {
      id: 'living',
      title: 'Living',
      image: getAssetUrl('static/img/shop-look-1.png'),
      alt: 'Living room furniture collection',
    },
    {
      id: 'dining',
      title: 'Dining',
      image: getAssetUrl('static/img/shop-look-2.png'),
      alt: 'Dining room furniture collection',
    },
    {
      id: 'bedroom',
      title: 'Bedroom',
      image: getAssetUrl('static/img/shop-look-3.png'),
      alt: 'Bedroom furniture collection',
    },
    {
      id: 'outdoor',
      title: 'Outdoor',
      image: getAssetUrl('static/img/shop-look-1.png'),
      alt: 'Outdoor furniture collection',
    },
    {
      id: 'rugs',
      title: 'Rugs',
      image: getAssetUrl('static/img/shop-look-2.png'),
      alt: 'Rugs collection',
    },
    {
      id: 'new-designs',
      title: 'New Designs',
      image: getAssetUrl('static/img/shop-look-3.png'),
      alt: 'New furniture designs collection',
    },
    {
      id: 'storage',
      title: 'Storage',
      image: getAssetUrl('static/img/shop-look-1.png'),
      alt: 'Storage furniture collection',
    }
  ], []);

  // Use provided collections or fallback to default
  const collectionsToRender = collections || defaultCollections;

  const handleCollectionClick = useCallback((collection) => {
    if (onCollectionClick) {
      onCollectionClick(collection);
    }
  }, [onCollectionClick]);

  const swiperOptions = useMemo(() => ({
    slidesPerView: 'auto',
    spaceBetween: 4,
    freeMode: true,
    grabCursor: true,
    navigation: !hideNavigation,
    breakpoints: {
      // Mobile
      320: {
        spaceBetween: 4,
        slidesPerView: 'auto',
      },
      // Desktop
      768: {
        spaceBetween: 4,
        slidesPerView: 'auto',
      },
    },
  }), []);

  return (
    <Box {...styles.container} {...props}>
      <Box {...styles.containerInner}>
        {/* Header Section */}
        <Flex {...styles.headerSection}>
          <Heading {...styles.title}>{title}</Heading>
          <Box {...styles.description}>
            <Text>{description}</Text>
          </Box>
        </Flex>

        {/* Collections List */}
        <Box {...styles.collectionsList}>
          <Swiper
            options={swiperOptions}
            presets=""
          >
            {collectionsToRender.map((collection) => (
              <Box
                key={collection.id}
                {...styles.collectionItem}
                cursor="pointer"
                onClick={() => handleCollectionClick(collection)}
              >
                <Box {...styles.collectionImageContainer}>
                  <Box
                    {...styles.collectionImage}
                    alt={collection.alt || collection.title}
                    as="img"
                    src={collection.image}
                  />
                  <Box {...styles.collectionOverlay}>
                    <Text {...styles.collectionLabel}>
                      {collection.title}
                    </Text>
                  </Box>
                </Box>
              </Box>
            ))}
          </Swiper>
        </Box>
      </Box>
    </Box>
  );
};

Collections.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  collections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ),
  onCollectionClick: PropTypes.func,
  hideNavigation: PropTypes.bool,
};

export default Collections;
